import { useQuery } from "@tanstack/react-query";
import { Pricing as PricingType } from "@shared/schema";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useState } from "react";
import { PricingModal } from "../components/PricingModal";

export default function Pricing() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const handleGetStarted = (tier: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedTier(tier);
    setIsModalOpen(true);
  };

  return (
    <div className="bg-white min-h-screen py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold mb-4">Licensing Options</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose the licensing option that best fits your project. We offer competitive rates 
            with bundle discounts for multiple tracks.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Basic Creator License */}
          <div className="pricing-card bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-transform hover:translate-y-[-5px] hover:shadow-lg">
            <div className="bg-primary-light p-6 text-white text-center">
              <h3 className="text-xl font-bold mb-1">Basic Creator License</h3>
              <p className="text-sm opacity-80">Perfect for content creators</p>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-3xl font-bold">
                  $49
                  <span className="text-sm font-normal text-gray-500">/track</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Bundle Discounts Available
                </p>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>YouTube videos & social media</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Podcast intro/outro music</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Single platform use only</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>12-month license duration</span>
                </li>
              </ul>
              <Button className="w-full py-6" size="lg" onClick={(e) => handleGetStarted('basic', e)}>
                Get Started
              </Button>
            </div>
          </div>

          {/* Professional License */}
          <div className="pricing-card bg-white rounded-xl shadow-lg overflow-hidden border-2 border-primary transition-transform hover:translate-y-[-5px] hover:shadow-lg">
            <div className="bg-primary p-6 text-white text-center relative">
              <div className="absolute top-[-5px] right-[-25px] bg-yellow-400 text-xs font-bold text-black px-8 py-1 transform rotate-45 shadow-md">
                POPULAR
              </div>
              <h3 className="text-xl font-bold mb-1">Professional License</h3>
              <p className="text-sm opacity-80">For professional creators</p>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-3xl font-bold">
                  $199
                  <span className="text-sm font-normal text-gray-500">/track</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">5-Track Bundle: $799 (Save $196)</p>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Monetized content across platforms</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Small business advertisements</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Mobile apps and games</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Perpetual license</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>All Basic features included</span>
                </li>
              </ul>
              <Button className="w-full py-6 bg-primary hover:bg-primary/90 text-black dark:text-white" size="lg" onClick={(e) => handleGetStarted('professional', e)}>
                Get Started
              </Button>
            </div>
          </div>

          {/* Enterprise License */}
          <div className="pricing-card bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-transform hover:translate-y-[-5px] hover:shadow-lg">
            <div className="bg-primary-light p-6 text-white text-center">
              <h3 className="text-xl font-bold mb-1">Enterprise License</h3>
              <p className="text-sm opacity-80">For major productions</p>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-3xl font-bold">
                  $499
                  <span className="text-sm font-normal text-gray-500">/track</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">10-Track Bundle: $3,999 (Save $991)</p>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>National/International commercials</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Major brand campaigns</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Feature films & broadcast shows</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>Multi-territory distribution</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <span>All Professional features included</span>
                </li>
              </ul>
              <Button className="w-full py-6" size="lg" onClick={(e) => handleGetStarted('enterprise', e)}>
                Get Started
              </Button>
            </div>
          </div>
        </div>

        {/* Bundle Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Bundle Offers</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 5-Track Bundle */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
              <h3 className="text-xl font-bold mb-4">5-Track Bundle</h3>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span>Basic License</span>
                  <span className="font-bold">$199</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Professional License</span>
                  <span className="font-bold">$799</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Enterprise License</span>
                  <span className="font-bold">$1,999</span>
                </div>
              </div>
              <div className="text-sm text-gray-600 italic">Save up to $496 compared to individual track pricing</div>
            </div>
            
            {/* 10-Track Bundle */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
              <h3 className="text-xl font-bold mb-4">10-Track Bundle</h3>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span>Basic License</span>
                  <span className="font-bold">$349</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Professional License</span>
                  <span className="font-bold">$1,499</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Enterprise License</span>
                  <span className="font-bold">$3,999</span>
                </div>
              </div>
              <div className="text-sm text-gray-600 italic">Save up to $991 compared to individual track pricing</div>
            </div>
          </div>
        </div>

        {/* Add-Ons Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Available Add-Ons</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-2">Extended Territory Rights</h3>
              <div className="mb-4">
                <span className="block text-gray-600 mb-1">Professional: +$149</span>
                <span className="block text-gray-600">Basic: +$299</span>
              </div>
              <p className="text-sm text-gray-600">Expand your license to cover additional territories</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-2">Broadcast Rights</h3>
              <div className="mb-4">
                <span className="block text-gray-600">Professional: +$299</span>
              </div>
              <p className="text-sm text-gray-600">Add TV, radio, and streaming platform rights</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
              <h3 className="text-lg font-bold mb-2">Custom Edit Service</h3>
              <div className="mb-4">
                <span className="block text-gray-600">All licenses: +$149</span>
              </div>
              <p className="text-sm text-gray-600">Get your track professionally edited to custom specifications</p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Need a custom licensing solution?</p>
          <Button variant="outline" size="lg" onClick={(e) => handleGetStarted('custom', e)}>
            Contact Us for Custom Pricing
          </Button>
        </div>
      </div>

      {isModalOpen && <PricingModal onClose={() => setIsModalOpen(false)} selectedTier={selectedTier} />}
    </div>
  );
}
